import React, { useEffect, useState } from "react";
import { _get, _post, _update } from "../../services/apiClient";
import { useNavigate } from "react-router-dom";
import { apiUrls } from "../../config/apiUrls.const";
import { getSecureItem } from "../../utils/LocalStorageManager";
import { allowOnlyNumbers } from "../../utils/AllowOnlyNumbers";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";
import { Button } from "react-bootstrap";
import MultiImageUpload from "../components/MultiImageUpload";
import { processAndMapImages } from "../../utils/ServiceAssetMapper";
import CreatableSelect from "react-select/creatable";

// 1. Unified Interface structures matching both Raw and Existing File reference layouts
interface ServiceImage {
  id?: number;              // Present if editing existing image structures from DB
  file: File | string;       // Holds a File instance (new) or structural Path String (existing)
  isDefault: boolean;
}

// React Select option structure: { value: string, label: string }
interface SelectOption {
  value: string;
  label: string;
}

interface Item {
  title: string;
  price: string;
  tags: SelectOption[];
  inputValue: string; // 💡 Add this line
  duration: {
    hr: any;
    min: any;
  };
  images: ServiceImage[];
}

interface Group {
  label: string;
  slug: string;
  categories: any[];
}

export default function ServiceForm() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const biz_code = getSecureItem("binfo")?.[0]?.biz_code;
  const [categorization, setCategorization] = useState<any[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<any>();
  const [groups, setGroups] = useState<Group[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [subcategories, setSubcategories] = useState<any[]>([]);

  const [categoryId, setCategoryId] = useState<any | null>(null);
  const [subcategoryId, setSubcategoryId] = useState<number | null>(null);

  // Initial state update
  const [items, setItems] = useState<Item[]>([
    { title: "", price: "", tags: [], inputValue: "", duration: { hr: '', min: '' }, images: [] }
  ]);

  useEffect(() => {
    const fetchBizCategorization = async () => {
      setLoading(true);
      try {
        const res: any = await _post(apiUrls.getBusinessCategorization, { biz_code: biz_code });
        if (res?.data?.success) {
          const data = res?.data?.data;
          setCategorization(data);
          let getGroups: any = [];
          Object.keys(data).forEach((group) => {
            getGroups.push({
              label: group.toUpperCase(),
              slug: group,
              categories: data[group]
            });
          });
          setGroups(getGroups);
          if (getGroups?.length === 1) {
            setCategories(getGroups[0]?.categories);
          }
        }
      } catch (err) {
        setError("Failed to fetch categorized items");
      } finally {
        setLoading(false);
      }
    };

    fetchBizCategorization();
  }, [biz_code]);

  function updateGroup(e: any) {
    const groupSlug = e.target.value;
    setCategories(categorization[groupSlug]);
    setSelectedGroup(e.target.value);
  }

  useEffect(() => {
    if (categoryId) {
      categories.forEach((cat: any) => {
        if (cat?.category_id === categoryId) {
          setSubcategories(cat?.subcategories);
        }
      });
    }
  }, [categoryId, categories]);

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const rawValue = e.target.value;

    if (!rawValue) {
      setCategoryId(null);
      setSubcategoryId(null);
      setSubcategories([]);
      return;
    }

    const selectedCatId = Number(rawValue);
    setCategoryId(selectedCatId);
    setSubcategoryId(null);

    const matchedCategory = categories.find((cat: any) => cat.category_id == selectedCatId);

    if (matchedCategory && matchedCategory.subcategories) {
      setSubcategories(matchedCategory.subcategories);
    } else {
      setSubcategories([]);
    }
  };

  const handleItemChange = (index: number, field: keyof Item, value: any) => {
    if (field !== "images" && field !== "duration") {
      const updated = [...items];
      (updated[index] as any)[field] = value;
      setItems(updated);
    }
  };

  const addItem = () => {
    if (items?.length <= 9)
      setItems([...items, { title: "", price: "", tags: [], inputValue: "", duration: { hr: '', min: '' }, images: [] }]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  function setItemDuration(e: any, index: number, target: string) {
    const updated = [...items];
    if (target === 'hr') {
      updated[index].duration.hr = e.target.value ? e.target.value : '';
    } else {
      updated[index].duration.min = e.target.value ? e.target.value : '';
    }
    setItems(updated);
  }

  // ==========================================
  // REACT-SELECT CREATABLE TAGS EVENT HANDLER
  // ==========================================
  const handleTagsChange = (index: number, newValue: any) => {
    const updated = [...items];
    updated[index].tags = newValue || [];
    setItems(updated);
  };

  const handleSubmit = async () => {
    setSaving(true);

    try {
      const formattedItems = await Promise.all(
        items.map(async (item) => {
          const duration = item.duration.hr && item.duration.hr !== 0
            ? `${item.duration.hr}:${item.duration.min}m`
            : `${item.duration.min}m`;

          const processedImages = await processAndMapImages(item.images, biz_code);

          // Map React-Select object value strings directly into a clean flat string array for your backend
          const stringArrayTags = item.tags.map(t => t.value.trim()).filter(t => t !== "");

          return {
            title: item.title,
            price: item.price,
            tags: stringArrayTags, // Transmitted perfectly as ["facial", "skin"]
            duration: duration,
            images: processedImages
          };
        })
      );

      const finalPayload = {
        biz_code: biz_code,
        category_id: categoryId,
        subcategory_id: subcategoryId,
        items: formattedItems
      };

      const res: any = await _post(apiUrls.createItem, finalPayload, {
        headers: { "Content-Type": "application/json" },
      });

      if (res.data?.success) {
        await Swal.fire({ icon: "success", title: "Saved!", showConfirmButton: false, timer: 1500 });
        navigate(-1);
      }
    } catch (err: any) {
      Swal.fire("Error", err.message || "Failed to save services.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid p-0">
      <h1 className="h3 mb-3">
        Create Service
        <Button className="float-end" variant="dark" size="sm" onClick={() => navigate(-1)}>
          Back
        </Button>
      </h1>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">

              {groups && groups?.length >= 2 && (
                <div className="row">
                  <div className="col-12">
                    {groups.map((group: any) => (
                      <div className="form-check form-check-inline" key={group?.slug}>
                        <input
                          className="form-check-input radio-squar"
                          name="group"
                          type="radio"
                          id={group?.slug}
                          value={group?.slug}
                          onChange={(e) => updateGroup(e)}
                        />
                        <label className="form-check-label" htmlFor={group?.slug}>
                          {group?.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="row bg-light p-2 rounded mb-3">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label fw-bold">Category</label>
                    <select
                      className="form-select"
                      value={categoryId ?? ""}
                      onChange={handleCategoryChange}
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.category_id}>
                          {cat.category?.name_en}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label fw-bold">Subcategory</label>
                    <select className="form-select"
                      value={subcategoryId ?? ""}
                      onChange={(e) => setSubcategoryId(e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">Select Subcategory</option>
                      {subcategories.map((sub) => (
                        <option key={sub.id} value={sub.subcategory_id}>
                          {sub?.subcategory?.name_en}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <hr />
              <h5 className="mb-3 text-secondary">Service Items Configuration Matrix</h5>

              {items.map((item, index) => (
                <div className="card border p-3 mb-3 bg-white shadow-sm" key={index}>
                  <div className="row g-2 align-items-start">

                    <div className="col-md-3">
                      <label className="form-label fw-bold">Service Title</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Title"
                        value={item.title}
                        onChange={(e) => handleItemChange(index, "title", e.target.value)}
                      />
                    </div>

                    <div className="col-md-2">
                      <label className="form-label fw-bold">Price</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Price"
                          value={item.price}
                          onKeyDown={allowOnlyNumbers}
                          onChange={(e) => handleItemChange(index, "price", e.target.value)}
                        />
                        <span className="input-group-text small">BDT</span>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <label className="form-label fw-bold">Duration allocation</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="hr"
                          value={item?.duration?.hr}
                          onKeyDown={allowOnlyNumbers}
                          onChange={(e) => setItemDuration(e, index, 'hr')}
                        />
                        <span className="input-group-text small">hr</span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="min"
                          value={item?.duration?.min}
                          onKeyDown={allowOnlyNumbers}
                          onChange={(e) => setItemDuration(e, index, 'min')}
                        />
                        <span className="input-group-text small">min</span>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label fw-bold">Media Gallery Assets</label>
                      <MultiImageUpload
                        indexId={index}
                        images={item.images}
                        maxFiles={3}
                        maxSizeMB={1}
                        acceptedTypes="image/jpg,image/jpeg,image/png,image/webp"
                        onChange={(updatedImages) => {
                          setItems(prev => {
                            const updated = [...prev];
                            updated[index].images = updatedImages;
                            return updated;
                          });
                        }}
                      />
                    </div>

                  </div>

                  {/* Clean, Modern React-Select Creatable Chip Row */}
                  <div className="row mt-3">
                    <div className="col-md-12">
                      <label className="form-label fw-bold d-block mb-1">Search Context Tags</label>
                      <CreatableSelect
                        isMulti
                        value={item.tags}
                        onChange={(newValue) => handleTagsChange(index, newValue)}
                        placeholder="Type a tag and press Enter or Comma..."
                        components={{ DropdownIndicator: null }}
                        menuIsOpen={false}
                        openMenuOnFocus={false}
                        openMenuOnClick={false}

                        // 💡 Controlled Input Properties
                        inputValue={item.inputValue || ""}
                        onInputChange={(text, actionMeta) => {
                          // Only update text if it's changing via user typing input
                          if (actionMeta.action === "input-change") {
                            const updated = [...items];
                            updated[index].inputValue = text;
                            setItems(updated);
                          }
                        }}

                        onKeyDown={(e: any) => {
                          if (e.key === "," || e.key === "Enter") {
                            e.preventDefault();
                            const val = item.inputValue.trim();

                            if (val) {
                              const currentOptions = item.tags || [];
                              if (!currentOptions.some(o => o.value.toLowerCase() === val.toLowerCase())) {
                                const updated = [...items];
                                // Add the tag AND wipe out the internal input state value safely
                                updated[index].tags = [...currentOptions, { value: val, label: val }];
                                updated[index].inputValue = "";
                                setItems(updated);
                              } else {
                                // If duplicate, just clear out the duplicate text
                                const updated = [...items];
                                updated[index].inputValue = "";
                                setItems(updated);
                              }
                            }
                          }
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '38px',
                            borderColor: '#dee2e6',
                            boxShadow: 'none',
                            '&:hover': { borderColor: '#b1b8c0' }
                          }),
                          multiValue: (base) => ({
                            ...base,
                            backgroundColor: '#6c757d',
                            borderRadius: '4px',
                          }),
                          multiValueLabel: (base) => ({
                            ...base,
                            color: '#ffffff',
                            fontWeight: '500',
                            paddingLeft: '6px',
                            paddingRight: '6px'
                          }),
                          multiValueRemove: (base) => ({
                            ...base,
                            color: '#ffffff',
                            ':hover': {
                              backgroundColor: '#dc3545',
                              color: '#ffffff',
                              borderRadius: '0 4px 4px 0'
                            },
                          }),
                        }}
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-md btn-danger position-absolute p-0 m-0"
                    style={{ top: "0px", right: "0px", zIndex: 10, width: "24px", height: "24px", fontWeight: "bold" }}
                    onClick={() => removeItem(index)}
                    title="Remove row item"
                  >
                    X
                  </button>
                </div>
              ))}

              <button className="btn btn-sm btn-dark mb-3" onClick={addItem}>
                <FeatherIcon icon="plus" size={14} className="me-1" /> Add Additional Item Row
              </button>

              <hr />
              <div className="text-end">
                <button className="btn btn-lg btn-primary px-5" disabled={saving} onClick={handleSubmit}>
                  {saving ? "Saving Components..." : "Save System Service"}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>

    </div>
  );
}